import { HTMLAttributes, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LinkProps, useNavigate } from "@tanstack/react-router";
import { confirmSignIn, fetchMFAPreference } from "aws-amplify/auth";
import { pipe } from "remeda";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { StatusButton } from "@/components/ui/loading-button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PasswordInput } from "@/components/password-input";
import type { AuthChallengeType, NewPasswordFormData } from "../../types";

type NewPasswordFormProps = HTMLAttributes<HTMLDivElement> & {
  isSuperadmin: boolean;
};

const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  patterns: [
    { regex: /[A-Z]/, message: "one uppercase letter" },
    { regex: /[a-z]/, message: "one lowercase letter" },
    { regex: /[0-9]/, message: "one number" },
    { regex: /[^A-Za-z0-9]/, message: "one special character" },
  ],
} as const;

const formSchema = z
  .object({
    newPassword: z
      .string()
      .min(PASSWORD_REQUIREMENTS.minLength, {
        message: `Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`,
      })
      .refine(
        (password) =>
          PASSWORD_REQUIREMENTS.patterns.every((pattern) =>
            pattern.regex.test(password),
          ),
        {
          message: `Password must contain at least ${PASSWORD_REQUIREMENTS.patterns
            .map((p) => p.message)
            .join(", ")}`,
        },
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

const authStepToRoute = (
  step: AuthChallengeType,
  isSuperadmin: boolean,
): LinkProps["to"] | undefined => {
  switch (step) {
    case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
      return isSuperadmin ? "/root-user/otp" : "/otp";
    case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
      return isSuperadmin ? "/root-user/totp-setup" : "/totp-setup";
    default:
      toast.error(`Unhandled auth step: ${step}`);
  }
};

const handleNextStep = async (
  isSuperadmin: boolean,
): Promise<LinkProps["to"]> => {
  try {
    const mfaPreference = await fetchMFAPreference();
    const hasTOTP =
      mfaPreference.preferred === "TOTP" ||
      (mfaPreference.enabled?.includes("TOTP") ?? false);

    if (!hasTOTP) {
      return isSuperadmin ? "/root-user/totp-setup" : "/totp-setup";
    }

    return isSuperadmin ? "/root-user/otp" : "/otp";
  } catch (error) {
    console.error("Error checking MFA status:", error);
    // Default to TOTP setup if we can't determine MFA status
    return isSuperadmin ? "/root-user/totp-setup" : "/totp-setup";
  }
};

export function NewPasswordForm({
  className,
  isSuperadmin,
  ...props
}: Readonly<NewPasswordFormProps>) {
  const navigate = useNavigate();
  const form = useForm<NewPasswordFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data: NewPasswordFormData) => {
    try {
      setIsSubmitting(true);
      const { nextStep } = await pipe(data.newPassword, (password) =>
        confirmSignIn({ challengeResponse: password }),
      );

      if (nextStep.signInStep === "DONE") {
        const route = await handleNextStep(isSuperadmin);
        await navigate({ to: route });
      } else {
        const route = authStepToRoute(
          nextStep.signInStep as AuthChallengeType,
          isSuperadmin,
        );
        if (route) void navigate({ to: route });
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Unknown error occurred",
        { description: "Error changing password" },
      );
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-2">
            {(["newPassword", "confirmPassword"] as const).map((fieldName) => (
              <FormField
                key={fieldName}
                control={form.control}
                name={fieldName}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {fieldName === "newPassword" ? "New" : "Confirm New"}{" "}
                      Password
                    </FormLabel>
                    <FormControl>
                      <PasswordInput placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
            <StatusButton
              type="submit"
              className="mt-4"
              isLoading={isSubmitting}
            >
              Change Password
            </StatusButton>
          </div>
        </form>
      </Form>
    </div>
  );
}
